/**
 * API client — thin fetch wrapper for the BFF. All calls use relative
 * /api/v1/* paths (proxied to the BFF in dev, same-origin in prod).
 * The Firebase ID token is attached as a Bearer token by the auth layer
 * (added in Phase 2); for now this is an unauthenticated skeleton.
 */
import type { ZodType } from 'zod';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly correlationId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  /** Extra headers to merge (e.g. dev-tools simulate-fail). */
  extraHeaders?: Record<string, string>;
}

/** Get the current Firebase ID token (for protected BFF routes). */
async function authToken(): Promise<string | null> {
  try {
    const { auth } = await import('./firebase.js');
    const { getIdToken } = await import('firebase/auth');
    const user = auth.currentUser;
    if (!user) return null;
    // Do NOT force-refresh (getIdToken(user, true)) — the SDK refreshes
    // automatically when the token is near expiry. Forcing a refresh on every
    // request invites sporadic network/refresh failures that then cascade into
    // page-level hydration failures.
    return await getIdToken(user);
  } catch {
    return null;
  }
}

async function request<T>(path: string, opts: RequestOptions = {}, responseSchema?: ZodType<T>): Promise<T> {
  const token = await authToken();
  const res = await fetch(path, {
    method: opts.method ?? 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts.extraHeaders ?? {}),
    },
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  });

  // 204 No Content
  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const json = text ? JSON.parse(text) : undefined;

  if (!res.ok) {
    throw new ApiError(
      res.status,
      json?.error ?? 'unknown_error',
      json?.message ?? 'Request failed',
      json?.correlationId,
    );
  }

  if (responseSchema) {
    const parsed = responseSchema.safeParse(json);
    if (!parsed.success) {
      throw new ApiError(res.status, 'invalid_response', 'Response did not match expected shape');
    }
    return parsed.data;
  }
  return json as T;
}

export const api = {
  get: <T>(path: string, schema?: ZodType<T>, signal?: AbortSignal) =>
    request<T>(path, { method: 'GET', signal }, schema),
  post: <T>(path: string, body?: unknown, schema?: ZodType<T>, extraHeaders?: Record<string, string>) =>
    request<T>(path, { method: 'POST', body, extraHeaders }, schema),
  put: <T>(path: string, body?: unknown, schema?: ZodType<T>, extraHeaders?: Record<string, string>) =>
    request<T>(path, { method: 'PUT', body, extraHeaders }, schema),
  patch: <T>(path: string, body?: unknown, schema?: ZodType<T>) =>
    request<T>(path, { method: 'PATCH', body }, schema),
  delete: <T>(path: string, schema?: ZodType<T>) => request<T>(path, { method: 'DELETE' }, schema),
};

/**
 * Fetch a path as a Blob (for file downloads — e.g. the data export). Attaches
 * the Firebase ID token as a Bearer token. Returns the Blob + the filename from
 * the Content-Disposition header (if present). Throws an ApiError on non-2xx.
 */
export async function downloadBlob(path: string): Promise<{ blob: Blob; filename: string | null }> {
  const token = await authToken();
  const res = await fetch(path, {
    method: 'GET',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!res.ok) {
    const text = await res.text();
    const json = text ? JSON.parse(text) : undefined;
    throw new ApiError(
      res.status,
      json?.error ?? 'unknown_error',
      json?.message ?? 'Download failed',
      json?.correlationId,
    );
  }
  const blob = await res.blob();
  // Parse the filename from Content-Disposition (attachment; filename="...").
  const cd = res.headers.get('Content-Disposition') ?? '';
  const match = cd.match(/filename="?([^"]+)"?/);
  return { blob, filename: match ? match[1]! : null };
}